import { useMemo } from 'react';
import {
	getApplicationForProject,
	type Application,
} from '@jira/platform__application-editions/src/application.tsx';
import {
	getEditionForProject,
	type ApplicationEdition,
} from '@jira/platform__application-editions/src/edition.tsx';
import {
	CORE_PROJECT,
	SOFTWARE_PROJECT,
	toProjectType,
} from '@jira/platform__project-types/src/project-types.tsx';
import { useSiteHasOnlyJira } from '@atlassian/jira-billing/src/controllers/explicitly-licensed-products/use-site-has-only-jira.tsx';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';

type ProductAndEditionInfo = {
	currentProduct?: Application;
	currentEdition?: ApplicationEdition;
	jiraEdition?: ApplicationEdition;
};

export const useJiraProductAndEdition = (): ProductAndEditionInfo => {
	const appEditions = useAppEditions();
	const { loading, data: project } = useProject();
	const siteHasOnlyJira = useSiteHasOnlyJira();

	return useMemo(() => {
		if (loading) return {};

		const projectType = toProjectType(project?.type);
		if (projectType === CORE_PROJECT && !siteHasOnlyJira) {
			// We are supporting only JWM implicit licensing when the parent license is JSW
			// Do not show on core products if site does not have only Jira
			return {};
		}

		if (siteHasOnlyJira) {
			const jiraEdition = getEditionForProject(SOFTWARE_PROJECT, appEditions);
			return {
				currentProduct: getApplicationForProject(SOFTWARE_PROJECT),
				currentEdition: jiraEdition,
				jiraEdition,
			};
		}

		return {
			currentProduct: projectType && getApplicationForProject(projectType),
			currentEdition: projectType && getEditionForProject(projectType, appEditions),
			jiraEdition: getEditionForProject(SOFTWARE_PROJECT, appEditions),
		};
	}, [appEditions, loading, project?.type, siteHasOnlyJira]);
};
